import { RequestError } from "../client.ts";
import { recordRequestFailure } from "../request-failures.ts";

export type MultipartAttachment = { fieldName: string; file: File };

export const multipartConversationRequest = <Result>(
  path: string,
  payload: { idempotencyKey: string },
  attachments: readonly MultipartAttachment[],
  onProgress: (progress: number) => void,
): Promise<Result> =>
  new Promise((resolve, reject) => {
    const startedAt = performance.now();
    const request = new XMLHttpRequest();
    const report = (status: number | null, body?: unknown, errorCode?: "network_error" | "request_timeout" | "invalid_response"): void => {
      recordRequestFailure({
        path,
        method: "POST",
        status,
        body,
        errorCode,
        route: request.getResponseHeader?.("x-portal-route"),
        correlationId: request.getResponseHeader?.("x-request-id"),
        durationMs: performance.now() - startedAt,
      });
    };
    request.open("POST", path);
    request.withCredentials = true;
    request.setRequestHeader("Idempotency-Key", payload.idempotencyKey);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    request.onerror = () => {
      report(null, undefined, "network_error");
      reject(new TypeError("Conversation submission failed"));
    };
    request.ontimeout = () => {
      report(null, undefined, "request_timeout");
      reject(new TypeError("Conversation submission timed out"));
    };
    request.onabort = () => reject(new DOMException("Conversation submission cancelled", "AbortError"));
    request.onload = () => {
      let body: unknown = null;
      try {
        body = request.responseText ? (JSON.parse(request.responseText) as unknown) : null;
      } catch {
        body = request.responseText;
        if (request.status >= 200 && request.status < 300) report(request.status, undefined, "invalid_response");
      }
      if (request.status < 200 || request.status >= 300) {
        report(request.status, body);
        reject(new RequestError(request.status, body));
        return;
      }
      resolve(body as Result);
    };
    const formData = new FormData();
    formData.set("payload", JSON.stringify(payload));
    for (const attachment of attachments) formData.append(attachment.fieldName, attachment.file, attachment.file.name);
    request.send(formData);
  });
